import type { ReactNode } from 'react';
import type { FieldMetadata, ObjectMetadata } from '../../../data/client';
import { cx } from '../../../ui';
import { PlaceholderCard, RecordCard } from './RecordCard';
import { CARD_SECONDARY_FIELDS, type TableColumn } from './tableModel';
import { usePresentRows } from './usePresentRows';

/**
 * The phone shape of the record list: full-width rows separated by hairlines, not
 * a desktop grid squeezed into 360px. Twenty ships the grid and loses; the sort
 * and the column choice still apply here, because the columns a user keeps are
 * exactly the values they want on the card.
 */
export interface RecordCardListProps {
  readonly object: ObjectMetadata;
  readonly columns: readonly TableColumn[];
  /** Every field of the object, for the row's touch-and-hold menu. */
  readonly fields: readonly FieldMetadata[];
  readonly boardGroupFieldId: string | null;
  /** `undefined` while the page loads. */
  readonly recordIds: readonly string[] | undefined;
  /** D21's Archived segment, shipped at 375px in the same change as the table. */
  readonly archived?: boolean | undefined;
  /**
   * THE STATUS ROW as the list's FINAL `<li>` (`table-completion.md` D11): the same component
   * the desktop renders as a table row, in the same `<ul>` so it scrolls with the cards.
   */
  readonly footer?: ReactNode;
}

const PLACEHOLDER_CARDS = ['1', '2', '3', '4', '5', '6'] as const;

export function RecordCardList({
  object,
  columns,
  fields,
  boardGroupFieldId,
  recordIds,
  archived = false,
  footer,
}: RecordCardListProps) {
  const secondary = columns.filter((column) => !column.isTitle).slice(0, CARD_SECONDARY_FIELDS);
  const loading = recordIds === undefined;
  const presence = usePresentRows(recordIds ?? []);

  return (
    /**
     * `pb-20` CLEARS THE FLOATING BAR AT THE BOTTOM OF THE SCREEN, and only when there is
     * something under the last card to clear. It was written for the create circle, which
     * wave 3 removed (PH-2), and the tab bar it also cleared is still there and still floats:
     * a list that ENDS with the status row ends with a control the reader cannot scroll past.
     * PH-1 gives every phone screen ONE safe-area inset owned by the shell, and this local
     * padding is what that inset replaces; until then it stays, because taking it away now
     * would put the count under the tab bar. Without a footer the last thing in the list is a
     * card whose own link fills it, and the spacing stays what it was.
     */
    <div className={cx(footer !== undefined && 'pb-20')}>
      <ul
        aria-busy={loading ? true : undefined}
        className="divide-y divide-line-subtle border-y border-line"
      >
        {loading
          ? PLACEHOLDER_CARDS.map((key) => (
              <PlaceholderCard key={key} fieldCount={secondary.length} />
            ))
          : presence.ids.map((recordId) => (
              <RecordCard
                key={recordId}
                object={object}
                fields={fields}
                boardGroupFieldId={boardGroupFieldId}
                secondary={secondary}
                columns={columns}
                recordId={recordId}
                present={presence.present(recordId)}
                onExited={() => presence.exited(recordId)}
                archived={archived}
              />
            ))}
        {footer}
      </ul>
    </div>
  );
}
